import { useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import {
  createDirectoryTag,
  deactivateDirectoryTag,
  reactivateDirectoryTag,
  renameDirectoryTag,
  type DirectoryTagKind,
  type DirectoryTagView,
} from '../directory-tags.api';

/** Add, rename, deactivate and reactivate on one catalogue. A form failure stays inline, row actions toast; `reload` re-fetches the list after each. */
export function useDirectoryTagActions(kind: DirectoryTagKind, reload: () => Promise<void>) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function save(action: () => Promise<unknown>, successMessage: string): Promise<boolean> {
    setError(null);
    setSaving(true);
    try {
      await action();
      await reload();
      toast.success(successMessage);
      return true;
    } catch {
      setError('Could not save — the name may already be in the list.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  return {
    error,
    saving,
    clearError: () => setError(null),
    add: (name: string) => save(() => createDirectoryTag(kind, name), `${name} added`),
    rename: (tag: DirectoryTagView, name: string) => save(() => renameDirectoryTag(tag.id, name), `${name} renamed`),
    deactivate: (tag: DirectoryTagView) => rowAction(() => deactivateDirectoryTag(tag.id), `${tag.name} deactivated`, `Couldn't deactivate ${tag.name}`),
    reactivate: (tag: DirectoryTagView) => rowAction(() => reactivateDirectoryTag(tag.id), `${tag.name} reactivated`, `Couldn't reactivate ${tag.name}`),
  };
}
